import React from 'react';
import { Icon, Avatar } from './components.jsx';
import { USERS } from './data.jsx';
import { NdConnectionCard } from './requests.jsx';
// settings.jsx - Admin Settings page for Triage.
//
// Left-rail navigation with a content pane on the right. Adding a new
// section = one entry in SECTIONS[] + one component below. Kept intentionally
// simple - no routing per section, just useState.
//
// Categories are stored in SQL (Categories table). The global `CATEGORIES`
// array in data.jsx is mutated in-place on load + after each CRUD operation
// so every existing reference (tickets.jsx, kb.jsx, stats.jsx, NewTicketModal,
// etc.) automatically picks up the change on next render. No prop-drilling
// refactor required.

  const { useState, useCallback, useEffect } = React;

  const SECTIONS = [
    { id: 'categories',   icon: 'tag',      label: 'Categories',       hint: 'Ticket categories used across the app' },
    { id: 'team',         icon: 'users',    label: 'IT Team',          hint: 'Members of the IT security group' },
    { id: 'appearance',   icon: 'sparkle',  label: 'Appearance',       hint: 'Accent colour, layout density' },
    { id: 'wallboard',    icon: 'bar',      label: 'Wallboard',        hint: 'Full-screen ops view for a wall-mounted display' },
    { id: 'integrations', icon: 'link',     label: 'Integrations',     hint: 'External services Triage connects to (NetDocuments, etc.)' },
    { id: 'about',        icon: 'info',     label: 'About',            hint: 'Version, deploy info, useful links, recent changes' },
  ];

  // True on phone-sized screens - the section rail becomes a horizontal tab
  // strip and the content pane goes full-width. Desktop/tablet keep the rail.
  function useIsPhone() {
    const q = '(max-width: 600px)';
    const [phone, setPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(q).matches);
    useEffect(() => {
      if (!window.matchMedia) return undefined;
      const mq = window.matchMedia(q);
      const on = () => setPhone(mq.matches);
      mq.addEventListener('change', on);
      return () => mq.removeEventListener('change', on);
    }, []);
    return phone;
  }

  function SettingsView({ state, dispatch, reloadCategories, staffList, tweaks, setTweak, currentUser }) {
    // Selected section - plain useState, no URL routing. Keeping it local
    // so refreshing Settings always lands on the same first section.
    const [section, setSection] = useState('categories');
    const isPhone = useIsPhone();

    return (
      <div style={{ display: 'flex', flexDirection: isPhone ? 'column' : 'row', height: '100%', background: 'var(--bg)' }}>
        {/* Section nav - a left rail on desktop, a horizontal tab strip on phone */}
        <aside style={isPhone ? {
          flexShrink: 0, display: 'flex', gap: 6, overflowX: 'auto',
          WebkitOverflowScrolling: 'touch', borderBottom: '1px solid var(--line)',
          background: 'var(--bg-elev)', padding: '10px 12px',
        } : {
          width: 240, flexShrink: 0,
          borderRight: '1px solid var(--line)',
          background: 'var(--bg-elev)',
          padding: '20px 10px', boxSizing: 'border-box',
          overflowY: 'auto',
        }}>
          {!isPhone && (
            <div style={{
              padding: '0 12px 14px', fontSize: 11, letterSpacing: 0.6,
              textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700,
            }}>Settings</div>
          )}
          {SECTIONS.map(s => {
            const active = section === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: isPhone ? 'auto' : '100%',
                  flexShrink: isPhone ? 0 : undefined,
                  whiteSpace: isPhone ? 'nowrap' : undefined,
                  padding: '9px 12px',
                  border: 'none', borderRadius: 6, marginBottom: isPhone ? 0 : 2,
                  cursor: 'pointer',
                  background: active ? 'var(--accent, #0D9488)' : (isPhone ? 'var(--bg-sub)' : 'transparent'),
                  color: active ? '#fff' : 'var(--text)',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: active ? 600 : 500,
                  textAlign: 'left',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-sub)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = isPhone ? 'var(--bg-sub)' : 'transparent'; }}
              >
                <Icon name={s.icon} size={13}/>
                <span>{s.label}</span>
              </button>
            );
          })}
        </aside>

        {/* Right pane - the selected section fills this */}
        <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
          {/* Extra bottom padding so the last panel in a section (esp. About →
              "Get help" once "Recent changes" pushes the layout down) isn't
              clipped by the scroll container edge. */}
          <div style={{ padding: isPhone ? '16px 14px 40px' : '28px 40px 64px', maxWidth: 880, boxSizing: 'border-box' }}>
            {(() => {
              const meta = SECTIONS.find(s => s.id === section);
              return (
                <div style={{ marginBottom: 22 }}>
                  <h1 style={{
                    margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)',
                    fontFamily: 'Poppins, var(--font), sans-serif', letterSpacing: -0.3,
                  }}>{meta?.label}</h1>
                  <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                    {meta?.hint}
                  </div>
                </div>
              );
            })()}

            {section === 'categories' && <CategoriesPanel state={state} dispatch={dispatch} reloadCategories={reloadCategories}/>}
            {section === 'team'       && <TeamPanel staffList={staffList} currentUser={currentUser}/>}
            {section === 'appearance' && <AppearancePanel tweaks={tweaks} setTweak={setTweak}/>}
            {section === 'wallboard'    && <WallboardPanel dispatch={dispatch}/>}
            {section === 'integrations' && <IntegrationsPanel/>}
            {section === 'about'        && <AboutPanel/>}
          </div>
        </div>
      </div>
    );
  }

  // Categories: full CRUD is enabled. Subcategories are still hardcoded in
  // data.jsx and edited via a follow-up PR - this panel manages only the
  // parent categories. Deleting a category leaves existing tickets referencing
  // its raw id - they render the id string in place of the label until moved
  // to another category.
  const READ_ONLY = false;

  function CategoriesPanel({ state, dispatch, reloadCategories }) {
    const [editing, setEditing] = useState(null);   // { id, label, sortOrder } | null
    const [creating, setCreating] = useState(false); // { id, label, sortOrder }
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const categories = state.categories || [];

    // Re-fetch after any mutation so the panel reflects server truth (covers
    // the case where two admins edit simultaneously).
    const refresh = useCallback(async () => {
      try { await reloadCategories(); } catch (_) { /* logged by reloader */ }
    }, [reloadCategories]);

    const onCreate = useCallback(async (form) => {
      setSaving(true); setError('');
      try {
        await window.IT_AUTH.api('POST', '/categories', form, 'settings');
        await refresh();
        setCreating(false);
      } catch (e) {
        setError(e.message || 'Create failed');
      } finally {
        setSaving(false);
      }
    }, [refresh]);

    const onSaveEdit = useCallback(async (id, updates) => {
      setSaving(true); setError('');
      try {
        await window.IT_AUTH.api('PATCH', `/categories/${id}`, updates, 'settings');
        await refresh();
        setEditing(null);
      } catch (e) {
        setError(e.message || 'Save failed');
      } finally {
        setSaving(false);
      }
    }, [refresh]);

    const onDelete = useCallback(async (cat) => {
      if (!window.confirm(`Delete category "${cat.label}"?\n\nThe server blocks this if any tickets still reference it - reassign or trash those first.`)) return;
      setSaving(true); setError('');
      try {
        await window.IT_AUTH.api('DELETE', `/categories/${cat.id}`, undefined, 'settings');
        await refresh();
      } catch (e) {
        setError(e.message || 'Delete failed');
      } finally {
        setSaving(false);
      }
    }, [refresh]);

    return (
      <div style={{
        background: 'var(--bg-card, #fff)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: '20px 22px',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Ticket categories</h2>
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
              The categories shown in the ticket category dropdown. Lower sort order appears first.
            </div>
          </div>
          {!READ_ONLY && !creating && (
            <button
              onClick={() => setCreating({ id: '', label: '' })}
              style={{
                fontSize: 13, fontWeight: 600, padding: '7px 14px', borderRadius: 6,
                background: 'var(--accent, #0D9488)', color: '#fff', border: 'none',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >+ New category</button>
          )}
        </div>

        {READ_ONLY && (
          <div style={{
            background: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E',
            padding: '10px 14px', borderRadius: 6, fontSize: 13, marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontWeight: 700 }}>Read-only.</span>
            <span>Subcategories management is in progress. Editing will be enabled once it's complete.</span>
          </div>
        )}

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {creating && (
          <CategoryRow
            mode="create"
            initial={creating}
            onCancel={() => { setCreating(false); setError(''); }}
            onSave={onCreate}
            saving={saving}
            existingIds={categories.map(c => c.id)}
          />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {categories.length === 0 && !creating && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
              No categories yet. Click "+ New category" to add one.
            </div>
          )}
          {categories.map(cat => editing?.id === cat.id ? (
            <CategoryRow
              key={cat.id}
              mode="edit"
              initial={editing}
              onCancel={() => { setEditing(null); setError(''); }}
              onSave={updates => onSaveEdit(cat.id, updates)}
              saving={saving}
              existingIds={categories.map(c => c.id).filter(id => id !== cat.id)}
            />
          ) : (
            <CategoryWithSubs
              key={cat.id}
              cat={cat}
              onEdit={() => setEditing({ id: cat.id, label: cat.label })}
              onDelete={() => onDelete(cat)}
              refresh={refresh}
              readOnly={READ_ONLY}
            />
          ))}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Category row + expandable subcategory list. Click the chevron to open,
  // add / rename / delete subcategories inline. Backend endpoints:
  //   POST   /api/subcategories          { categoryId, label, sortOrder? }
  //   PATCH  /api/subcategories/{id}     { label?, sortOrder?, categoryId? }
  //   DELETE /api/subcategories/{id}
  // On every mutation we call refresh() (the parent's reloadCategories)
  // which re-fetches /categories with subcategories embedded and updates
  // both state.categories and the global SUBCATS map.
  function CategoryWithSubs({ cat, onEdit, onDelete, refresh, readOnly }) {
    const [open, setOpen]         = useState(false);
    const [addingSub, setAddingSub] = useState(false);
    const [editingSub, setEditingSub] = useState(null); // { id, label, sortOrder }
    const [subErr, setSubErr]     = useState('');
    const [subSaving, setSubSaving] = useState(false);
    const subs = Array.isArray(cat.subcategories) ? cat.subcategories : [];

    const doSubMutation = async (fn) => {
      setSubSaving(true); setSubErr('');
      try {
        await fn();
        await refresh();
        setAddingSub(false);
        setEditingSub(null);
      } catch (e) {
        setSubErr(e.message || 'Save failed');
      } finally {
        setSubSaving(false);
      }
    };

    const createSub = (form) => doSubMutation(() =>
      window.IT_AUTH.api('POST', '/subcategories', { categoryId: cat.id, ...form }, 'settings'));
    const saveSub = (id, form) => doSubMutation(() =>
      window.IT_AUTH.api('PATCH', `/subcategories/${id}`, form, 'settings'));
    const deleteSub = (sub) => {
      if (!window.confirm(`Delete subcategory "${sub.label}"?\n\nThe server blocks this if any tickets still reference it - reassign or trash those first.`)) return;
      doSubMutation(() =>
        window.IT_AUTH.api('DELETE', `/subcategories/${sub.id}`, undefined, 'settings'));
    };

    return (
      <div style={{ borderTop: '1px solid var(--line)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 8px' }}>
          <button
            onClick={() => setOpen(o => !o)}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 4, display: 'inline-flex', alignItems: 'center',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 120ms', color: 'var(--text-muted)',
            }}
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            <Icon name="chevron" size={12}/>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{cat.label}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--mono)', marginTop: 2 }}>
              id: {cat.id} · {subs.length} subcategor{subs.length === 1 ? 'y' : 'ies'}
            </div>
          </div>
          {!readOnly && (
            <>
              <button onClick={onEdit} style={btnGhost}>Edit</button>
              <button onClick={onDelete} style={{ ...btnGhost, color: '#EF4444' }}>Delete</button>
            </>
          )}
        </div>

        {open && (
          <div style={{
            padding: '4px 8px 14px 42px',
            background: 'var(--bg-sub)',
            borderTop: '1px solid var(--line)',
          }}>
            {subErr && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', padding: '6px 10px', borderRadius: 5, fontSize: 12, margin: '8px 0' }}>
                {subErr}
              </div>
            )}

            {subs.length === 0 && !addingSub && (
              <div style={{ padding: '10px 0', fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                No subcategories yet.
              </div>
            )}

            {subs.map(sub => editingSub?.id === sub.id ? (
              <SubRow
                key={sub.id}
                mode="edit"
                initial={editingSub}
                saving={subSaving}
                onCancel={() => { setEditingSub(null); setSubErr(''); }}
                onSave={form => saveSub(sub.id, form)}
              />
            ) : (
              <div key={sub.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '7px 0', borderTop: '1px dashed var(--line)',
              }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text)' }}>
                  {sub.label}
                </div>
                {!readOnly && (
                  <>
                    <button
                      onClick={() => setEditingSub({ id: sub.id, label: sub.label })}
                      style={{ ...btnGhost, padding: '3px 8px', fontSize: 11 }}
                    >Edit</button>
                    <button
                      onClick={() => deleteSub(sub)}
                      style={{ ...btnGhost, padding: '3px 8px', fontSize: 11, color: '#EF4444' }}
                    >Delete</button>
                  </>
                )}
              </div>
            ))}

            {addingSub && (
              <SubRow
                mode="create"
                initial={{ label: '' }}
                saving={subSaving}
                onCancel={() => { setAddingSub(false); setSubErr(''); }}
                onSave={createSub}
              />
            )}

            {!readOnly && !addingSub && (
              <div style={{ marginTop: 10 }}>
                <button
                  onClick={() => setAddingSub(true)}
                  style={{ ...btnGhost, padding: '5px 12px', fontSize: 12 }}
                >+ Add subcategory</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Inline row for creating / editing a subcategory. Same shape as CategoryRow
  // but slimmer - no id field (auto-assigned by the DB) and no reparenting
  // control (subcategories move via delete-and-recreate to keep the UI simple).
  function SubRow({ mode, initial, saving, onCancel, onSave }) {
    const [form, setForm] = useState(initial);
    const isCreate = mode === 'create';
    const valid = !!String(form.label || '').trim() && !saving;
    // No sort field - the API always returns rows alphabetically by label,
    // so users don't manage position. We still send sortOrder=100 so the
    // DB column stays populated, but it's out of the UI.
    const submit = () => {
      if (!valid) return;
      onSave({ label: form.label.trim(), sortOrder: 100 });
    };
    return (
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr auto auto',
        gap: 8, alignItems: 'center', padding: '10px 0',
        borderTop: '1px dashed var(--line)',
      }}>
        <input
          value={form.label || ''}
          onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
          placeholder="Subcategory label"
          style={{ ...inp, padding: '5px 8px', fontSize: 12 }}
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel(); }}
        />
        <button onClick={onCancel} style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }}>Cancel</button>
        <button
          onClick={submit}
          disabled={!valid}
          style={{
            ...btnPrimary, padding: '4px 12px', fontSize: 11,
            opacity: valid ? 1 : 0.5, cursor: valid ? 'pointer' : 'not-allowed',
          }}
        >{saving ? 'Saving…' : (isCreate ? 'Add' : 'Save')}</button>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Team panel - read-only view of the IT security group. Data comes from
  // the STAFF_LIST loaded at boot (Graph enumeration of the IT group). The
  // frontend USERS constant in data.jsx has the "canonical" IT team (with
  // avatar colours, initials, short slugs used as assigneeIds). We match on
  // email to combine both sources.
  function TeamPanel({ staffList, currentUser }) {
    const meEmail = (currentUser?.mail || currentUser?.userPrincipalName || '').toLowerCase();
    // Merge USERS (canonical, has avatar colour + slug) with staffList
    // (Graph, has job title). USERS is the source of truth for who's on
    // the assignee dropdown across the app - that's what's shown here.
    const teamRows = USERS.map(u => {
      const graph = (staffList || []).find(s => (s.mail || '').toLowerCase() === u.email.toLowerCase());
      return {
        ...u,
        jobTitle: graph?.jobTitle || u.title || null,
        isYou: u.email.toLowerCase() === meEmail,
      };
    });

    return (
      <div style={panelCard}>
        <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-muted)' }}>
          {teamRows.length} member{teamRows.length !== 1 ? 's' : ''}. Membership is managed in Entra;
          this list reflects whoever is currently in the IT security group.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {teamRows.map(u => (
            <div key={u.id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 8px', borderTop: '1px solid var(--line)',
            }}>
              <Avatar user={u} size="sm"/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                  {u.name}
                  {u.isYou && (
                    <span style={{
                      marginLeft: 8, fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                      textTransform: 'uppercase', color: 'var(--accent, #0D9488)',
                      background: 'rgba(13, 148, 136, 0.1)', padding: '2px 6px', borderRadius: 3,
                    }}>you</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {u.jobTitle && <>{u.jobTitle} · </>}
                  <span style={{ fontFamily: 'var(--mono)' }}>{u.email}</span>
                </div>
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                color: 'var(--text-muted)', fontFamily: 'var(--mono)',
              }}>{u.id}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Appearance panel - exposes the tweaks that were previously only editable
  // via the dev-mode TweaksPanel (Ctrl+K on the tweak panel button). Same
  // useTweaks hook, driven by props from App so the values live in one place.
  function AppearancePanel({ tweaks, setTweak }) {
    const ACCENTS = ["#0D9488","#5B6CFF","#2D7AEA","#1F8A5B","#C24E63","#252525"];
    const t = tweaks || {};
    const theme = t.theme || 'light';
    return (
      <div style={panelCard}>
        <div style={{ marginBottom: 20 }}>
          <div style={settingLbl}>Theme</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { id: 'light', label: 'Light' },
              { id: 'dark',  label: 'Dark' },
              { id: 'auto',  label: 'Auto' },
            ].map(v => (
              <button
                key={v.id}
                onClick={() => setTweak && setTweak('theme', v.id)}
                style={{
                  padding: '7px 14px', borderRadius: 5, cursor: 'pointer',
                  fontSize: 13, fontFamily: 'inherit', fontWeight: 600,
                  background: theme === v.id ? 'var(--accent, #0D9488)' : 'transparent',
                  color: theme === v.id ? '#fff' : 'var(--text)',
                  border: theme === v.id ? 'none' : '1px solid var(--line)',
                }}
              >{v.label}</button>
            ))}
          </div>
          <div style={settingHint}>
            Auto follows your operating system's light/dark setting and switches
            automatically when it does.
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={settingLbl}>Accent colour</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {ACCENTS.map(c => (
              <button
                key={c}
                onClick={() => setTweak && setTweak('accent', c)}
                title={c}
                style={{
                  width: 32, height: 32, borderRadius: 6, cursor: 'pointer',
                  background: c, border: t.accent === c ? '3px solid var(--text)' : '2px solid var(--line)',
                  padding: 0,
                }}
                aria-label={`Set accent to ${c}`}
              />
            ))}
          </div>
          <div style={settingHint}>
            Used for buttons, active links, and highlights.
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={settingLbl}>Layout density</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['comfortable', 'compact'].map(v => (
              <button
                key={v}
                onClick={() => setTweak && setTweak('density', v)}
                style={{
                  padding: '7px 14px', borderRadius: 5, cursor: 'pointer',
                  fontSize: 13, fontFamily: 'inherit', fontWeight: 600,
                  background: t.density === v ? 'var(--accent, #0D9488)' : 'transparent',
                  color: t.density === v ? '#fff' : 'var(--text)',
                  border: t.density === v ? 'none' : '1px solid var(--line)',
                  textTransform: 'capitalize',
                }}
              >{v}</button>
            ))}
          </div>
          <div style={settingHint}>Compact tightens row padding across lists.</div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <ToggleRow
            label="Show priority bars"
            hint="Coloured stripe on the left of each ticket row."
            value={!!t.showPriority}
            onChange={v => setTweak && setTweak('showPriority', v)}
          />
        </div>
        <ToggleRow
          label="Sidebar counts"
          hint="Show the number badge next to each sidebar item."
          value={!!t.sidebarCount}
          onChange={v => setTweak && setTweak('sidebarCount', v)}
        />
      </div>
    );
  }

  function ToggleRow({ label, hint, value, onChange }) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
          {hint && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{hint}</div>}
        </div>
        <button
          role="switch"
          aria-checked={value}
          onClick={() => onChange(!value)}
          style={{
            width: 40, height: 22, borderRadius: 999, border: 'none', padding: 0,
            background: value ? 'var(--accent, #0D9488)' : '#CBD5E1',
            cursor: 'pointer', position: 'relative', transition: 'background 120ms',
            flexShrink: 0,
          }}
        >
          <span style={{
            position: 'absolute', top: 2, left: value ? 20 : 2,
            width: 18, height: 18, borderRadius: '50%', background: '#fff',
            transition: 'left 120ms',
          }}/>
        </button>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Wallboard launcher - the tile that used to sit at the bottom of the old
  // Settings page. Kept the same visual treatment; just moved into its own
  // section so it stops competing for attention with Categories.
  function WallboardPanel({ dispatch }) {
    return (
      <div style={{
        padding: '22px 24px',
        background: 'linear-gradient(135deg, #0a1220 0%, #134E4A 100%)',
        border: '1px solid var(--line)', borderRadius: 10, color: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{
              margin: 0, fontSize: 16, fontWeight: 700, color: '#fff',
              fontFamily: 'Poppins, var(--font), sans-serif', letterSpacing: -0.2,
            }}>Operations wallboard</h2>
            <div style={{ marginTop: 6, fontSize: 13, color: 'rgba(255,255,255,0.72)', lineHeight: 1.55 }}>
              Full-screen monitoring view for a wall-mounted display - open tickets,
              unassigned queue, team load, recent activity. Auto-refreshes every 30 seconds.
              Press <kbd style={{
                background: 'rgba(255,255,255,0.15)', padding: '1px 6px',
                borderRadius: 3, fontSize: 11, fontFamily: 'inherit',
              }}>Esc</kbd> from the wallboard to return.
            </div>
          </div>
          <button
            onClick={() => dispatch({ type: 'navigate', view: 'wallboard' })}
            style={{
              background: '#0D9488', color: '#fff', border: 'none',
              padding: '10px 18px', borderRadius: 6, fontWeight: 700,
              fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
              flexShrink: 0,
            }}
          >Open wallboard →</button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Integrations panel - external services Triage connects to. NetDocuments
  // OAuth connection lives here (was originally on User Requests admin, but
  // that page is about triaging incoming requests - the connection status
  // + reconnect button belongs with other admin config).
  function IntegrationsPanel() {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>NetDocuments</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted, #6B7280)' }}>
            OAuth connection Triage uses to apply WSM changes on approved user
            requests. Bound to a dedicated service account so all automated
            actions appear as that identity in NetDocuments audit logs, not
            as a specific person.
          </p>
        </div>
        <NdConnectionCard/>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // About panel - build/deploy info, API health, useful links. Version /
  // deploy timestamp aren't easily injected without a build-time step, so
  // we lean on the live API health check for "is the backend up" and link
  // out to GitHub for the actual commit / deploy trail.
  function AboutPanel() {
    const [health, setHealth] = useState('checking');
    const [healthTime, setHealthTime] = useState(null);
    const pingHealth = useCallback(() => {
      setHealth('checking');
      window.IT_AUTH.api('GET', '/health', undefined, 'settingsAbout')
        .then(r => {
          setHealth(r?.ok ? 'ok' : 'error');
          setHealthTime(r?.time || null);
        })
        .catch(() => { setHealth('error'); setHealthTime(null); });
    }, []);
    useEffect(() => { pingHealth(); }, [pingHealth]);

    // Recent changes - the last 10 commits, baked into dist/latest-commits.json
    // at deploy time by the GitHub Actions workflow. No runtime token required.
    // Fails gracefully if the file's missing (first run before the workflow
    // change lands) - we just don't render the section rather than showing
    // an error banner.
    const [commits, setCommits] = useState(null);
    useEffect(() => {
      fetch('/latest-commits.json', { cache: 'no-cache' })
        .then(r => r.ok ? r.json() : null)
        .then(data => setCommits(Array.isArray(data) ? data : []))
        .catch(() => setCommits([]));
    }, []);

    const statusColor = health === 'ok' ? '#059669' : health === 'error' ? '#DC2626' : '#6B7280';
    const statusText = health === 'ok' ? 'Online' : health === 'error' ? 'Unreachable' : 'Checking…';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={panelCard}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Backend status</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
            <span style={{
              display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
              background: statusColor, flexShrink: 0,
            }}/>
            <span style={{ color: statusColor, fontWeight: 600 }}>{statusText}</span>
            {healthTime && (
              <span style={{ color: 'var(--text-muted)', fontSize: 12, fontFamily: 'var(--mono)' }}>
                · server clock: {new Date(healthTime).toLocaleString('en-GB')}
              </span>
            )}
            <button onClick={pingHealth} style={{ ...btnGhost, marginLeft: 'auto' }}>Re-check</button>
          </div>
        </div>

        <div style={panelCard}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>App info</div>
          <InfoRow label="Live URL" value={<a href="https://triage.leeandthompson.com" target="_blank" rel="noreferrer" style={infoLink}>triage.leeandthompson.com</a>}/>
          <InfoRow label="Source" value={<a href="https://github.com/leeandthompson/triage" target="_blank" rel="noreferrer" style={infoLink}>github.com/leeandthompson/triage</a>}/>
          <InfoRow label="Deploy" value="Every push to main via GitHub Actions"/>
          <InfoRow label="Auth" value="Microsoft Entra SSO · IT security group gated"/>
          <InfoRow label="Hosting" value="Azure Static Web Apps + Azure Functions v4"/>
        </div>

        {commits && commits.length > 0 && (() => {
          // Turn commit subjects into something a non-developer can scan.
          // Filter out merge commits (they add no info), map the leading
          // "Prefix:" into a coloured tag, capitalise the rest. If a
          // subject has no prefix, tag it 'Change'.
          const TAGS = {
            perf:        { label: 'Speed',      color: '#0891B2' },
            fix:         { label: 'Fix',        color: '#DC2626' },
            fixed:       { label: 'Fix',        color: '#DC2626' },
            bug:         { label: 'Fix',        color: '#DC2626' },
            feature:     { label: 'New',        color: '#059669' },
            feat:        { label: 'New',        color: '#059669' },
            add:         { label: 'New',        color: '#059669' },
            settings:    { label: 'Settings',   color: '#7C3AED' },
            categories:  { label: 'Categories', color: '#7C3AED' },
            rota:        { label: 'Rota',       color: '#F59E0B' },
            digest:      { label: 'Email',      color: '#F59E0B' },
            cors:        { label: 'Security',   color: '#DB2777' },
            pagination:  { label: 'Tickets',    color: '#2563EB' },
            tickets:     { label: 'Tickets',    color: '#2563EB' },
            ticket:      { label: 'Tickets',    color: '#2563EB' },
          };
          const humanise = (subject) => {
            const m = subject.match(/^([A-Za-z]+):\s*(.+)$/);
            if (!m) return { tag: { label: 'Change', color: '#6B7280' }, text: subject };
            const [, prefix, rest] = m;
            const tag = TAGS[prefix.toLowerCase()] || { label: prefix, color: '#6B7280' };
            const text = rest.charAt(0).toUpperCase() + rest.slice(1);
            return { tag, text };
          };
          const rows = commits
            .filter(c => !/^Merge /.test(c.message || ''))
            .map(c => ({ ...c, humanised: humanise(c.message || '') }));
          if (rows.length === 0) return null;
          return (
            <div style={panelCard}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Recent changes</div>
                <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
                  Last {rows.length} update{rows.length !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {rows.map(c => (
                  <a
                    key={c.sha}
                    href={c.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '84px 1fr auto',
                      gap: 12, alignItems: 'center',
                      padding: '10px 4px',
                      borderTop: '1px solid var(--line)',
                      textDecoration: 'none', color: 'inherit',
                    }}
                    title={`${c.sha} · ${c.author}`}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-sub)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                      textTransform: 'uppercase', padding: '3px 8px',
                      borderRadius: 4, background: c.humanised.tag.color, color: '#fff',
                      textAlign: 'center', whiteSpace: 'nowrap',
                    }}>{c.humanised.tag.label}</span>
                    <div style={{
                      fontSize: 13, color: 'var(--text)', minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{c.humanised.text}</div>
                    <div style={{
                      fontSize: 11, color: 'var(--text-muted)',
                      whiteSpace: 'nowrap',
                    }}>
                      {c.date ? new Date(c.date).toLocaleString('en-GB', {
                        day: 'numeric', month: 'short',
                        hour: '2-digit', minute: '2-digit',
                      }) : ''}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          );
        })()}

        <div style={panelCard}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Get help</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Bugs or feature requests → use the <strong>Requests</strong> tab in the sidebar.
            Anything urgent → poke James or Rob directly.
          </div>
        </div>
      </div>
    );
  }

  function InfoRow({ label, value }) {
    return (
      <div style={{
        display: 'grid', gridTemplateColumns: '110px 1fr', gap: 12,
        padding: '6px 0', fontSize: 13, alignItems: 'baseline',
      }}>
        <div style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>{label}</div>
        <div style={{ color: 'var(--text)', minWidth: 0, overflowWrap: 'anywhere' }}>{value}</div>
      </div>
    );
  }

  // Shared form row for both create + edit. `mode` controls whether id is
  // editable (creating only - changing the id of an existing row would orphan
  // tickets that already reference it).
  function CategoryRow({ mode, initial, onCancel, onSave, saving, existingIds }) {
    const [form, setForm] = useState(initial);
    const isPhone = useIsPhone();
    const isCreate = mode === 'create';
    const normalisedId = isCreate
      ? String(form.id || '').trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_').slice(0, 50)
      : form.id;
    const idClash = isCreate && existingIds.includes(normalisedId);
    const idValid = !!normalisedId;
    const labelValid = !!String(form.label || '').trim();
    const canSave = idValid && labelValid && !idClash && !saving;

    const handleSave = () => {
      if (!canSave) return;
      // sortOrder still sent so the DB column stays populated, but it's
      // no longer surfaced in the UI - API returns rows alphabetically.
      const payload = { label: form.label.trim(), sortOrder: 100 };
      if (isCreate) payload.id = normalisedId;
      onSave(payload);
    };

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isPhone ? '1fr' : '180px 1fr auto auto',
          gap: 10, alignItems: 'center',
          padding: '14px 8px',
          borderTop: '1px solid var(--line)',
          background: 'var(--bg-sub)',
          borderRadius: 6,
        }}
      >
        <div>
          <label style={lbl}>ID</label>
          <input
            value={form.id || ''}
            disabled={!isCreate}
            onChange={e => setForm(f => ({ ...f, id: e.target.value }))}
            placeholder="e.g. NETWORKING"
            style={{ ...inp, fontFamily: 'var(--mono)', textTransform: 'uppercase', background: isCreate ? '#fff' : 'var(--bg-elev)' }}
          />
        </div>
        <div>
          <label style={lbl}>Label</label>
          <input
            value={form.label || ''}
            onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
            placeholder="e.g. Networking"
            style={inp}
            autoFocus
          />
        </div>
        <button onClick={onCancel} style={btnGhost}>Cancel</button>
        <button
          onClick={handleSave}
          disabled={!canSave}
          style={{
            ...btnPrimary,
            opacity: canSave ? 1 : 0.5,
            cursor: canSave ? 'pointer' : 'not-allowed',
          }}
        >{saving ? 'Saving…' : (isCreate ? 'Create' : 'Save')}</button>
        {idClash && (
          <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#EF4444', marginTop: 2 }}>
            ID "{normalisedId}" already exists. Pick a different ID.
          </div>
        )}
      </div>
    );
  }

  // Small style helpers - kept inline since this is the only file using them.
  // Input background uses --bg-elev so it stays readable in dark mode
  // (was hardcoded #fff which stayed bright-white against the dark canvas).
  const inp = {
    width: '100%', padding: '7px 9px', border: '1px solid var(--line)',
    borderRadius: 5, fontSize: 13, fontFamily: 'inherit', background: 'var(--bg-elev)',
    color: 'var(--text)', boxSizing: 'border-box', outline: 'none',
  };
  const lbl = {
    display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
    letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 3,
  };
  const btnGhost = {
    fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 5,
    background: 'transparent', border: '1px solid var(--line)',
    color: 'var(--text)', cursor: 'pointer', fontFamily: 'inherit',
  };
  const btnPrimary = {
    fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 5,
    background: 'var(--accent, #0D9488)', color: '#fff', border: 'none',
    fontFamily: 'inherit',
  };
  // Shared shell used by every section panel - keeps them visually consistent
  // (same padding, border, radius, background) without a whole CSS module.
  const panelCard = {
    background: 'var(--bg-card, #fff)',
    border: '1px solid var(--line)',
    borderRadius: 10,
    padding: '20px 22px',
  };
  const settingLbl = {
    fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8,
  };
  const settingHint = {
    fontSize: 12, color: 'var(--text-muted)', marginTop: 6,
  };
  const infoLink = { color: 'var(--accent, #0D9488)', textDecoration: 'none', fontWeight: 500 };

export { SettingsView };